import React, { createContext, useState, useEffect, useMemo } from 'react';
import { branchService } from '../services/branchService';
import { toast } from 'react-toastify';

export const BranchContext = createContext();

export const BranchProvider = ({ children }) => {
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('edit'); // 'edit' or 'create'
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    state: '',
  });
  
  // Load all branches once
  const loadBranches = async () => {
    setLoading(true);
    try {
      const response = await branchService.getBranches();
      if (response.data && response.data.success && response.data.data) {
        setAllBranches(response.data.data);
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('Error loading branches:', error);
      toast.error('Failed to load branches');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Load branches on component mount (only if user is authenticated)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadBranches();
    }
  }, []);

  // Update filters without making API calls
  const updateFilters = (newFilters) => {
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  };

  // Create a new branch
  const createBranch = async (branchData) => {
    setLoading(true);
    try {
      const response = await branchService.createBranch(branchData);
      if (response.data && response.data.success) {
        toast.success('Branch created successfully');
        await loadBranches(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error creating branch:', error);
      toast.error('Failed to create branch');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing branch
  const updateBranch = async (branchId, branchData) => {
    setLoading(true);
    try {
      const response = await branchService.updateBranch(branchId, branchData);
      if (response.data && response.data.success) {
        toast.success('Branch updated successfully');
        await loadBranches(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error updating branch:', error);
      toast.error('Failed to update branch');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete a branch
  const deleteBranch = async (branchId) => {
    setLoading(true);
    try {
      const response = await branchService.deleteBranch(branchId);
      if (response.data && response.data.success) {
        toast.success('Branch deleted successfully');
        await loadBranches(); // Reload the list
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error deleting branch:', error);
      toast.error('Failed to delete branch');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Open modal for editing
  const openEditModal = (branch) => {
    setSelectedBranch(branch);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  // Open modal for creating
  const openCreateModal = () => {
    setSelectedBranch(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBranch(null);
  };

  // Filter and sort branches locally based on current filters
  const filteredBranches = useMemo(() => {
    // First filter the branches
    const filtered = allBranches.filter(branch => {
      // Search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const matchesSearch = 
          (branch.name && branch.name.toLowerCase().includes(searchTerm)) || 
          (branch.address && branch.address.toLowerCase().includes(searchTerm)) ||
          (branch.branch_name && branch.branch_name.toLowerCase().includes(searchTerm));
        
        if (!matchesSearch) return false;
      }
      
      // City filter
      if (filters.city && filters.city !== '') {
        if (!branch.city_name || branch.city_name.toLowerCase() !== filters.city.toLowerCase()) return false;
      }
      
      // State filter
      if (filters.state && filters.state !== '') {
        if (!branch.state || branch.state.toLowerCase() !== filters.state.toLowerCase()) return false;
      }
      
      return true;
    });
    
    // Sort by name
    return filtered.sort((a, b) => {
      // Handle null/undefined values and convert to lowercase for case-insensitive comparison
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });
  }, [allBranches, filters]);

  // Get unique cities
  const cities = useMemo(() => {
    const citySet = new Set();
    allBranches.forEach(branch => {
      if (branch.city_name) {
        citySet.add(branch.city_name);
      }
    });
    return Array.from(citySet).sort();
  }, [allBranches]);

  // Get unique states
  const states = useMemo(() => {
    const stateSet = new Set();
    allBranches.forEach(branch => {
      if (branch.state) {
        stateSet.add(branch.state);
      }
    });
    return Array.from(stateSet).sort();
  }, [allBranches]);

  return (
    <BranchContext.Provider value={{ 
      branches: filteredBranches,
      filteredBranches, // Explicitly expose this for pagination
      allBranches,
      selectedBranch,
      isModalOpen,
      modalMode,
      loading,
      filters,
      cities,
      states,
      loadBranches,
      updateFilters,
      createBranch,
      updateBranch,
      deleteBranch,
      openEditModal,
      openCreateModal,
      closeModal
    }}>
      {children}
    </BranchContext.Provider>
  );
};
